import { Canvas, useThree } from '@react-three/fiber';
import { Preload, AdaptiveDpr, PerformanceMonitor } from '@react-three/drei';
import { SpinGallery } from '../components/gallery/SpinGallery';
import { ProjectDetails } from '../components/gallery/ProjectDetails';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { ScrollHelper, ClickHelper } from '../components/ui/Helpers';
import { LabMonitor } from '../components/ui/LabMonitor';
import { WebGLCapture } from '../components/ui/WebGLCapture';
import { useResize } from '../hooks/viewport/useResize';
import { CAMERA_CONFIG } from '../constants/config';
import { projects } from '../data/projects';
import { useState, useEffect, Suspense } from 'react';
import { useThetaMonitor } from '../hooks/monitoring/useThetaMonitor';
import { useSound } from '../hooks/audio/useSound';
import { ProjectIndicator } from '../components/ui/ProjectIndicator';
import { useAppStore } from '../store/useAppStore';
import * as THREE from 'three';

function PerformanceMonitorWrapper({
  children,
  flipflops,
}: {
  children: React.ReactNode;
  flipflops: number;
}) {
  const setDpr = useThree(s => s.setDpr);
  return (
    <PerformanceMonitor
      onIncline={() => setDpr(2)}
      onDecline={() => setDpr(1)}
      flipflops={flipflops}
    >
      {children}
    </PerformanceMonitor>
  );
}

interface HomeProps {
  allMediaLoaded: boolean;
}

export const Home: React.FC<HomeProps> = ({ allMediaLoaded }) => {
  const { width, height } = useResize();
  const [showGallery, setShowGallery] = useState(false);
  const selectedProject = useAppStore(state => state.selectedProject);
  const selectedIndex = useAppStore(state => state.selectedIndex);
  const nearestProjectIndex = useAppStore(state => state.nearestProjectIndex);
  const isTransitionComplete = useAppStore(state => state.isTransitionComplete);
  const isInitialAnimationComplete = useAppStore(state => state.isInitialAnimationComplete);
  const isHoveringProject = useAppStore(state => state.isHoveringProject);
  const hasVisitedAbout = useAppStore(state => state.hasVisitedAbout);
  const isMobile = window.innerWidth < 768;
  const showHeaderFooter = !selectedProject || !isMobile;
  const thetaRef = useThetaMonitor();
  const { updateWind, playProjectOpenWind, playProjectCloseWind, playUI } = useSound();

  useEffect(() => {
    const unsubscribe = useAppStore.subscribe((state, previousState) => {
      if (state.velocity === previousState.velocity) return;
      updateWind(state.velocity);
    });

    return unsubscribe;
  }, [updateWind]);

  useEffect(() => {
    if (hasVisitedAbout) {
      setShowGallery(true);
    } else {
      const timer = setTimeout(() => setShowGallery(true), 500);
      return () => clearTimeout(timer);
    }
  }, [hasVisitedAbout]);

  const setSelected = useAppStore(state => state.setSelected);
  const setScrollOffset = useAppStore(state => state.setScrollOffset);
  const setIndicatorScrolling = useAppStore(state => state.setIsIndicatorScrolling);
  const setVelocity = useAppStore(state => state.setVelocity);

  useEffect(() => {
    const n = projects.length;
    const anglePerProject = (Math.PI * 2) / n;
    const middleOffset = Math.floor(n / 2);

    const animateToIndex = (targetIndex: number) => {
      const currentOffset = useAppStore.getState().scrollOffset;
      const targetNormalized =
        ((middleOffset - targetIndex) % n + n) % n * anglePerProject;
      let targetOffset = targetNormalized / 2;

      const period = Math.PI;
      const diff = targetOffset - currentOffset;
      if (diff > period / 2) targetOffset -= period;
      else if (diff < -period / 2) targetOffset += period;

      const startOffset = currentOffset;
      const distance = targetOffset - startOffset;
      const duration = 800;
      const startTime = performance.now();
      const WIND_PEAK_VELOCITY = 0.009;

      setIndicatorScrolling(true);
      useAppStore.getState().setHasScrolled(true);

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const currentPosition = startOffset + distance * eased;
        setScrollOffset(currentPosition);

        const windVelocity = WIND_PEAK_VELOCITY * Math.sin(progress * Math.PI);
        setVelocity(windVelocity);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setIndicatorScrolling(false);
          setVelocity(0);
        }
      };

      requestAnimationFrame(animate);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isInteractiveTarget =
        !!target &&
        (target.tagName === 'BUTTON' ||
          target.tagName === 'A' ||
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);

      if (
        isInteractiveTarget &&
        event.key === 'Enter'
      ) {
        return;
      }

      if (!isInitialAnimationComplete) return;

      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        playUI('nav', 'click');
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const baseIndex = selectedIndex ?? nearestProjectIndex;
        const nextIndex = (baseIndex + direction + n) % n;
        if (selectedIndex !== null) {
          setSelected(null, null);
          playProjectCloseWind();
        }
        animateToIndex(nextIndex);
      } else if (event.key === 'Enter') {
        if (selectedIndex !== null) return;
        event.preventDefault();
        const indexToOpen = nearestProjectIndex;
        setSelected(projects[indexToOpen], indexToOpen);
        playProjectOpenWind();
      } else if (event.key === 'Escape') {
        if (selectedIndex === null) return;
        event.preventDefault();
        setSelected(null, null);
        playProjectCloseWind();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isInitialAnimationComplete,
    nearestProjectIndex,
    playProjectCloseWind,
    playProjectOpenWind,
    playUI,
    selectedIndex,
    setIndicatorScrolling,
    setScrollOffset,
    setSelected,
    setVelocity,
  ]);

  return (
    <div className="relative w-full h-full">
      <div
        className="sr-only"
        role="region"
        aria-label="Galerie de projets"
      >
        <h2>Projets</h2>
        <ul>
          {projects.map((p, i) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => setSelected(p, i)}
                tabIndex={-1}
                className="text-left underline focus:outline focus:ring-2 focus:ring-inset"
              >
                {p.title} – Projet {i + 1} sur {projects.length}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="fixed inset-0 w-full h-full overflow-hidden">
        {showHeaderFooter && (
          <>
            <Header />
            <Footer />
          </>
        )}
        <ProjectDetails />
        <Canvas
          camera={CAMERA_CONFIG}
          frameloop={selectedProject && isTransitionComplete ? 'demand' : 'always'}
          onCreated={({ invalidate }) => {
            useAppStore.getState().setInvalidate(invalidate);
          }}
          className="w-full h-full bg-[#f7f7f7]"
          style={{
            width,
            height,
            cursor: isHoveringProject ? 'pointer' : 'default',
            touchAction: !selectedProject ? 'none' : 'auto',
          }}
          dpr={[1, 2]}
          gl={{
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance',
            stencil: false,
            depth: true,
            outputColorSpace: THREE.SRGBColorSpace,
            // @ts-ignore - samples is a valid WebGL parameter but not typed in R3F
            samples: 4,
          }}
        >
          <PerformanceMonitorWrapper flipflops={3}>
            <Preload all />
            <AdaptiveDpr pixelated />
            <ambientLight intensity={0.5} />
            <pointLight position={[10, 10, 10]} />
            {showGallery && (
              <Suspense fallback={null}>
                <SpinGallery projects={projects} allMediaLoaded={allMediaLoaded} />
              </Suspense>
            )}
            <WebGLCapture />
          </PerformanceMonitorWrapper>
        </Canvas>
      </div>
      {!selectedProject && !isMobile && <LabMonitor thetaRef={thetaRef} />}
      {!isMobile && <ProjectIndicator projects={projects} />}
      <ScrollHelper />
      <ClickHelper />
    </div>
  );
};
